// Draft of the Roadmap "Create a ticket" drawer (OTC-3156). Kept per company and
// per signed-in user so a shared front-desk browser never shows one person's
// draft to another. Sign out clears it with the rest of localStorage.
import { isEmptyHtml } from '@/util/richText'

export interface IdName { id: number; name: string }

export interface RoadmapTicketDraft {
  v: 1
  ticket_type: IdName | null
  feature: IdName | null
  title: string
  answers: string[]
  description: string
  saved_at: string
}

const PREFIX = 'otc.roadmapTicketDraft'

export function draftStorageKey(companyId: unknown, userId: unknown): string | null {
  if (companyId === null || companyId === undefined || companyId === '') return null
  if (userId === null || userId === undefined || userId === '') return null
  return PREFIX + '.' + String(companyId) + '.' + String(userId)
}

// The key for whoever is signed in right now; null once sign out has cleared
// localStorage, so a page torn down by the sign-out redirect cannot write the
// draft back.
export function draftKeyFromStorage(): string | null {
  try {
    const profile = JSON.parse(localStorage.getItem('profile') || 'null')
    return draftStorageKey(localStorage.getItem('companyId'), profile ? profile.id : null)
  } catch (e) {
    return null
  }
}

type DraftFields = Omit<RoadmapTicketDraft, 'v' | 'saved_at'>

// Stable comparison of two form states; field order is fixed here.
export function draftFingerprint(d: DraftFields): string {
  return JSON.stringify([
    d.ticket_type ? d.ticket_type.id : null,
    d.feature ? d.feature.id : null,
    d.title || '',
    Array.isArray(d.answers) ? d.answers : [],
    d.description || '',
  ])
}

export const EMPTY_DRAFT_FINGERPRINT = draftFingerprint({ ticket_type: null, feature: null, title: '', answers: [], description: '' })

// An image-only answer is content: isEmptyHtml looks at text alone.
export function htmlHasContent(html: unknown): boolean {
  if (typeof html !== 'string' || html === '') return false
  return !isEmptyHtml(html) || /<img\b/i.test(html)
}

export function hasTypedContent(d: Pick<RoadmapTicketDraft, 'title' | 'answers' | 'description'>): boolean {
  return (typeof d.title === 'string' && d.title.trim() !== '')
    || (Array.isArray(d.answers) && d.answers.some(htmlHasContent))
    || htmlHasContent(d.description)
}

export function hasAnyContent(d: Pick<RoadmapTicketDraft, 'ticket_type' | 'feature' | 'title' | 'answers' | 'description'>): boolean {
  return !!d.ticket_type || !!d.feature || hasTypedContent(d)
}

function isIdName(x: any): x is IdName {
  return !!x && typeof x === 'object' && x.id !== undefined && typeof x.name === 'string'
}

export function readDraft(key: string | null): RoadmapTicketDraft | null {
  if (!key) return null
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const d = JSON.parse(raw)
    if (!d || d.v !== 1) return null
    return {
      v: 1,
      ticket_type: isIdName(d.ticket_type) ? { id: d.ticket_type.id, name: d.ticket_type.name } : null,
      feature: isIdName(d.feature) ? { id: d.feature.id, name: d.feature.name } : null,
      title: typeof d.title === 'string' ? d.title : '',
      answers: Array.isArray(d.answers) ? d.answers.map((a: unknown) => (typeof a === 'string' ? a : '')) : [],
      description: typeof d.description === 'string' ? d.description : '',
      saved_at: typeof d.saved_at === 'string' ? d.saved_at : '',
    }
  } catch (e) {
    console.error('Roadmap ticket draft could not be read', e)
    return null
  }
}

// Writes the draft, or removes the key when nothing is left in it.
export function writeDraft(key: string | null, d: Omit<RoadmapTicketDraft, 'v' | 'saved_at'>): void {
  if (!key) return
  try {
    if (!hasAnyContent(d)) {
      localStorage.removeItem(key)
      return
    }
    localStorage.setItem(key, JSON.stringify({ v: 1, ...d, saved_at: new Date().toISOString() }))
  } catch (e) {
    // Quota or blocked storage: the in-memory draft still survives a dismiss.
    console.error('Roadmap ticket draft could not be saved', e)
  }
}

export function removeDraft(key: string | null): void {
  if (!key) return
  try {
    localStorage.removeItem(key)
  } catch (e) {
    console.error('Roadmap ticket draft could not be removed', e)
  }
}
